import React, { useRef, useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Radio,  
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import axios from 'axios';
import { toast } from 'react-toastify';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useReactToPrint } from 'react-to-print';

import config from '../config/Config';
import { apiHeaderToken } from '../helper/My_Helper';

const SB_FormatEdit = ({ 
  candidateData, 
  refereeData, 
  initialFormData,
  onSave,
  onCancel 
}) => {
  const formRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the applied job data - find the job that matches
  // const appliedJob = candidateData?.applied_jobs?.find(
  //   item => item?.job_id === candidateData?.job_id
  // );
  const appliedJob =
  candidateData?.applied_jobs?.find(
    item => item?.approval_note_data?.note_id  && item?.form_status ==="Offer" || item?.form_status ==="Hired"// priority
  ) ||
  candidateData?.applied_jobs?.find(
    item => item?.job_id === candidateData?.job_id // fallback
  );
  // Helper to get address from permanent_address object
  const getAddress = (addressObj) => {
    if (typeof addressObj === 'string') return addressObj;
    if (addressObj?.address) return addressObj.address;
    return '';
  };

  // Initial values - properly map from candidateData
  const defaultValues = {
    // Candidate Details
    candidateName: initialFormData?.candidateName || candidateData?.name || '',
    candidateFatherName: initialFormData?.candidateFatherName || candidateData?.applicant_form_data?.father_hushband_name || '',
    candidateResidence: initialFormData?.candidateResidence || getAddress(candidateData?.applicant_form_data?.permanent_address) || '',
    candidatePosition: appliedJob?.job_designation || initialFormData?.candidatePosition || candidateData?.designation || '',
    candidateProject: initialFormData?.candidateProject || candidateData?.project_name || '',
    
    // Referee Details - from refereeData or references_other_than_family
    refereeName: initialFormData?.refereeName || refereeData?.name || candidateData?.applicant_form_data?.references_other_than_family?.[0]?.name || '',
    refereeFatherName: initialFormData?.refereeFatherName || refereeData?.father_name || '',
    refereeResidence: initialFormData?.refereeResidence || refereeData?.residence || '',
    refereeEmployment: initialFormData?.refereeEmployment || refereeData?.employment || '',
    refereeDesignation: initialFormData?.refereeDesignation || refereeData?.designation || candidateData?.applicant_form_data?.references_other_than_family?.[0]?.designation || '',
    refereeContact: initialFormData?.refereeContact || refereeData?.contact || candidateData?.applicant_form_data?.references_other_than_family?.[0]?.mobile || '',
    
    // Reference Details
    knownSince: initialFormData?.knownSince || '',
    relationToCandidate: initialFormData?.relationToCandidate || candidateData?.applicant_form_data?.references_other_than_family?.[0]?.relation || '',
    
    // Certificate Section
    certifierName: initialFormData?.certifierName || '',
    certifierDesignation: initialFormData?.certifierDesignation || '',
    certifierOrganization: initialFormData?.certifierOrganization || '',
    certifierContact: initialFormData?.certifierContact || '',
    certifierKnownSince: initialFormData?.certifierKnownSince || '',
    certifierCapacity: initialFormData?.certifierCapacity || '',
    
    // Quality Ratings
    qualities: initialFormData?.qualities || {
      abilityToPerform: '',
      professionalOutlook: '',
      sincerity: '',
      honesty: '',
      teamWork: '',
      discipline: '',
      anyOther: '',
      anyOtherRating: ''
    },
    
    // Recommendation
    recommendationText: initialFormData?.recommendationText || '',
    
    // Dates
    date: initialFormData?.date || moment().format('YYYY-MM-DD'),
    place: initialFormData?.place || '',
    
    // Signatures
    candidateSignature: initialFormData?.candidateSignature || candidateData?.applicant_form_data?.signature || '',
    refereeSignature: initialFormData?.refereeSignature || refereeData?.signature || '',
    certifierSignature: initialFormData?.certifierSignature || '',
    
    // Official Seal
    officialSeal: initialFormData?.officialSeal || '',
  };

  const validationSchema = Yup.object({
    candidateName: Yup.string().required('Candidate name is required'),
    candidateFatherName: Yup.string().required("Father's name is required"),
    candidateResidence: Yup.string().required('Residence address is required'),
    candidatePosition: Yup.string().required('Position is required'),
    refereeName: Yup.string().required("Referee's name is required"),
    refereeFatherName: Yup.string().required("Referee's father name is required"),
    refereeResidence: Yup.string().required("Referee's residence is required"),
    refereeEmployment: Yup.string().required("Referee's employment is required"),
    refereeDesignation: Yup.string().required("Referee's designation is required"),
    refereeContact: Yup.string().required("Referee's contact is required"),
    knownSince: Yup.string().required('Known since year is required'),
    relationToCandidate: Yup.string().required('Relation to candidate is required'),
    certifierName: Yup.string().required('Certifier name is required'),
    certifierDesignation: Yup.string().required('Certifier designation is required'),
    certifierOrganization: Yup.string().required('Organization is required'),
    certifierContact: Yup.string().required('Contact number is required'),
    certifierKnownSince: Yup.string().required('Years known is required'),
    certifierCapacity: Yup.string().required('Capacity/Relation is required'),
    recommendationText: Yup.string()
      .required('Recommendation is required')
      .max(500, 'Recommendation should be concise (max 500 characters)'),
    date: Yup.string().required('Date is required'),
    place: Yup.string().required('Place is required'),
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const payload = {
        candidate_id: candidateData?._id,
        applied_job_id: appliedJob?._id,
        ...values,
        date: moment(values.date).format('YYYY-MM-DD')
      };
      
      // API call to save SB Format - update with your actual endpoint
      const response = await axios.post(
        `${config.API_URL}saveSBFormat`,
        payload,
        apiHeaderToken()
      );
      
      toast.success(response?.data?.message || 'Reference testimonial saved successfully!');
      
      if (onSave) {
        onSave(values);
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save testimonial');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: formRef,
    pageStyle: `
      @media print {
        body { margin: 0; }
        .no-print { display: none !important; }
        .MuiPaper-root { box-shadow: none !important; padding: 0 !important; }
      }
    `
  });

  // Quality Rating Radio Component
  const QualityRating = ({ name, label, value, onChange }) => (
    <TableRow>
      <TableCell sx={{ border: '1px solid #ddd', p: 1.5, bgcolor: '#fafafa' }}>
        <Typography variant="body2" fontWeight="bold">{label}</Typography>
      </TableCell>
      {['excellent', 'veryGood', 'average', 'belowAverage'].map((option) => (
        <TableCell key={option} sx={{ border: '1px solid #ddd', p: 1, textAlign: 'center' }}>
          <Radio
            name={name}
            value={option}
            checked={value === option}
            onChange={onChange}
            size="small"
          />
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexWrap: 'wrap',
        gap: 1
      }} className="no-print">
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="small"
          >
            Print
          </Button>
        </Box>
      </Box>

      {/* Form */}
      <Box ref={formRef}>
        <Formik
          initialValues={defaultValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, handleChange, errors, touched }) => (
            <Form>
              <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
                {/* Header */}
                <Box sx={{ 
                  position: 'relative', 
                  mb: 3, 
                  pb: 2, 
                  // borderBottom: '2px solid #2c387e',
                  minHeight: '110px'
                }}>
                  <Box sx={{ position: 'absolute', left: 0, top: 0 }}>
                    <img 
                      src='/logo512.png' 
                      alt='HLFPPT Logo' 
                      height="70" 
                    />
                  </Box>
                  <Box sx={{ textAlign: 'center', pt: 1 }}>
                    <Typography 
                      variant="h5" 
                      fontWeight="bold" 
                      color="#2c387e" 
                      gutterBottom
                      sx={{ 
                        fontSize: { xs: '1.2rem', sm: '2rem', md: '2.25rem' },
                        '@media print': { 
                          fontSize: '28px !important' 
                        }
                      }}
                    >
                      REFERENCE TESTIMONIAL
                    </Typography>
                  </Box>
                </Box>

                {/* ===== PAGE 1: REFERENCE TESTIMONIAL ===== */}
                <Paper variant="outlined" sx={{ p: 3, mb: 3, border: 'none' }}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" paragraph sx={{ lineHeight: 2 }}>
                      <strong>Whereas</strong>, Hindustan Latex Family Planning Promotion Trust (hereinafter referred to as "the Trust") and
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Typography variant="body2"><strong>Mr./Ms.</strong></Typography>
                      <TextField
                        name="candidateName"
                        value={values.candidateName}
                        onChange={handleChange}
                        error={touched.candidateName && Boolean(errors.candidateName)}
                        helperText={touched.candidateName && errors.candidateName}
                        placeholder="Candidate Name"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '200px' }}
                      />
                      <Typography variant="body2"><strong>S/o / D/o</strong></Typography>
                      <TextField
                        name="candidateFatherName"
                        value={values.candidateFatherName}
                        onChange={handleChange}
                        error={touched.candidateFatherName && Boolean(errors.candidateFatherName)}
                        helperText={touched.candidateFatherName && errors.candidateFatherName}
                        placeholder="Father's Name"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '200px' }}
                      />
                      <Typography variant="body2"><strong>, a resident of</strong></Typography>
                    </Box>

                    <TextField
                      name="candidateResidence"
                      value={values.candidateResidence}
                      onChange={handleChange}
                      error={touched.candidateResidence && Boolean(errors.candidateResidence)}
                      helperText={touched.candidateResidence && errors.candidateResidence}
                      placeholder="Residential Address"
                      variant="standard"
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Typography variant="body2">
                        has been selected for a contractual appointment as a
                      </Typography>
                      <TextField
                        name="candidatePosition"
                        value={values.candidatePosition}
                        onChange={handleChange}
                        error={touched.candidatePosition && Boolean(errors.candidatePosition)}
                        helperText={touched.candidatePosition && errors.candidatePosition}
                        placeholder="Position Name"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '200px' }}
                      />
                    </Box>

                    <Typography variant="body2" paragraph sx={{ lineHeight: 2 }}>
                      to carry out such duties as are specified in his appointment letter, or as may be entrusted from time to time upon him/her and in the course of his/her carrying out his/her duties he/she would have to be entrusted with various brochures/materials/products/assets of the Organization as may be required to perform his/her job; and one of the preconditions for the appointment of in the Organization/Project is furnishing of this reference testimonial; and
                    </Typography>

                    <Typography variant="body2" paragraph sx={{ lineHeight: 2 }}>
                      <strong>Whereas</strong>, Mr./Ms. <strong>{values.candidateName}</strong> has approached me to stand as his/her references and
                    </Typography>

                    <Typography variant="body2" paragraph sx={{ lineHeight: 2 }}>
                      <strong>Whereas</strong>, the undersigned,
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Typography variant="body2"><strong>Shri</strong></Typography>
                      <TextField
                        name="refereeName"
                        value={values.refereeName}
                        onChange={handleChange}
                        error={touched.refereeName && Boolean(errors.refereeName)}
                        helperText={touched.refereeName && errors.refereeName}
                        placeholder="Referee Name"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '200px' }}
                      />
                      <Typography variant="body2"><strong>s/o</strong></Typography>
                      <TextField
                        name="refereeFatherName"
                        value={values.refereeFatherName}
                        onChange={handleChange}
                        error={touched.refereeFatherName && Boolean(errors.refereeFatherName)}
                        helperText={touched.refereeFatherName && errors.refereeFatherName}
                        placeholder="Father's Name"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '200px' }}
                      />
                      <Typography variant="body2"><strong>, a resident of</strong></Typography>
                    </Box>

                    <TextField
                      name="refereeResidence"
                      value={values.refereeResidence}
                      onChange={handleChange}
                      error={touched.refereeResidence && Boolean(errors.refereeResidence)}
                      helperText={touched.refereeResidence && errors.refereeResidence}
                      placeholder="Residence Address"
                      variant="standard"
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Typography variant="body2">in the employment of the</Typography>
                      <TextField
                        name="refereeEmployment"
                        value={values.refereeEmployment}
                        onChange={handleChange}
                        error={touched.refereeEmployment && Boolean(errors.refereeEmployment)}
                        helperText={touched.refereeEmployment && errors.refereeEmployment}
                        placeholder="Name of PSU or Govt Department"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '200px' }}
                      />
                      <Typography variant="body2">as</Typography>
                      <TextField
                        name="refereeDesignation"
                        value={values.refereeDesignation}
                        onChange={handleChange}
                        error={touched.refereeDesignation && Boolean(errors.refereeDesignation)}
                        helperText={touched.refereeDesignation && errors.refereeDesignation}
                        placeholder="Rank/Designation"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '150px' }}
                      />
                      <Typography variant="body2">and is willing to stand references for</Typography>
                      <Typography variant="body2"><strong>{values.candidateName}</strong></Typography>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" paragraph sx={{ lineHeight: 2 }}>
                        I, hereby agree to give my reference for the above candidate, that the candidate is known to me in person since from
                      </Typography>
                      <TextField
                        name="knownSince"
                        value={values.knownSince}
                        onChange={handleChange}
                        error={touched.knownSince && Boolean(errors.knownSince)}
                        helperText={touched.knownSince && errors.knownSince}
                        placeholder="Year (e.g., 2015)"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '150px', mb: 1 }}
                      />
                      <Typography variant="body2" paragraph sx={{ lineHeight: 2, mt: 1 }}>
                        and bears a good moral character. He/She has not being convicted by any court of law or litigant incisive to the best of my knowledge and belief. In case of any impersonification, misappropriation, fraud or financial irregularities, breach of confidentiality etc. reported against the candidate during the course of his/her employment with the organization; the management may invoke this reference and take appropriate action against the candidate. I also understand that in case of any such abovementioned act indulged by the candidate even after leaving the employment of your Organization; your management reserves all right to approach his/her prospective/next employer seeking appropriate action against him/her without prejudice to your organization's rights and contentions.
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" fontWeight="bold">Relation to Candidate:</Typography>
                      <TextField
                        name="relationToCandidate"
                        value={values.relationToCandidate}
                        onChange={handleChange}
                        error={touched.relationToCandidate && Boolean(errors.relationToCandidate)}
                        helperText={touched.relationToCandidate && errors.relationToCandidate}
                        placeholder="e.g., Colleague, Supervisor, Family Friend"
                        variant="standard"
                        size="small"
                        fullWidth
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Box>

                  {/* Signatures Section - Page 1 */}
                  <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid #2c387e' }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Specimen Signature of Candidate
                        </Typography>
                        <Box sx={{ 
                          borderBottom: '2px solid #000', 
                          minHeight: '70px', 
                          display: 'flex', 
                          alignItems: 'flex-end',
                          p: 1,
                          bgcolor: '#fafafa'
                        }}>
                          {values.candidateSignature ? (
                            <img 
                              src={`${config.IMAGE_PATH}${values.candidateSignature}`} 
                              alt="Candidate Signature" 
                              style={{ maxHeight: '60px', width: 'auto' }} 
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">No signature available</Typography>
                          )}
                        </Box>
                        <Typography variant="caption" fontWeight="bold" sx={{ mt: 1, display: 'block' }}>
                          Shri {values.candidateName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Signature of Referee
                        </Typography>
                        <TextField
                          name="refereeSignature"
                          value={values.refereeSignature}
                          onChange={handleChange}
                          placeholder="Referee Signature (text or upload)"
                          variant="standard"
                          fullWidth
                          sx={{ 
                            borderBottom: '2px solid #000',
                            bgcolor: '#fafafa',
                            p: 1
                          }}
                        />
                        <Typography variant="caption" fontWeight="bold" sx={{ mt: 1, display: 'block' }}>
                          {values.refereeName}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>

                {/* ===== PAGE 2: CERTIFICATE ===== */}
                <Paper variant="outlined" sx={{ p: 3, mt: 3, border: 'none' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#2c387e' }}>
                    CERTIFICATE
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 2, color: '#666' }}>
                    (To be filled in by a Gazetted Officer in State/Central Government Service/ Officers in PSUS/ Officers in Nationalized Banks/ Principals in Government aided schools)
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" paragraph sx={{ lineHeight: 2 }}>
                      I have known <strong>{values.candidateName}</strong> resident of
                    </Typography>
                    <TextField
                      name="candidateResidence"
                      value={values.candidateResidence}
                      onChange={handleChange}
                      placeholder="Residence"
                      variant="standard"
                      size="small"
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      <Typography variant="body2">since</Typography>
                      <TextField
                        name="certifierKnownSince"
                        value={values.certifierKnownSince}
                        onChange={handleChange}
                        error={touched.certifierKnownSince && Boolean(errors.certifierKnownSince)}
                        helperText={touched.certifierKnownSince && errors.certifierKnownSince}
                        placeholder="Years"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '100px' }}
                      />
                      <Typography variant="body2">years in my capacity as</Typography>
                      <TextField
                        name="certifierCapacity"
                        value={values.certifierCapacity}
                        onChange={handleChange}
                        error={touched.certifierCapacity && Boolean(errors.certifierCapacity)}
                        helperText={touched.certifierCapacity && errors.certifierCapacity}
                        placeholder="Capacity/Relation"
                        variant="standard"
                        size="small"
                        sx={{ minWidth: '200px' }}
                      />
                      <Typography variant="body2">to the candidate. As regards the candidate, I rank him as indicated here with reference to the professional and personal qualities.</Typography>
                    </Box>

                    {/* Quality Table */}
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 3, mb: 3 }}>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold', bgcolor: '#f0f0f0', p: 1.5 }}>
                              Quality/Attribute
                            </TableCell>
                            {['Excellent', 'Very Good', 'Average', 'Below Average'].map((label) => (
                              <TableCell key={label} sx={{ border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', bgcolor: '#f0f0f0', p: 1.5 }}>
                                {label}
                              </TableCell>
                            ))}
                          </TableRow>
                          <QualityRating
                            name="qualities.abilityToPerform"
                            label="Ability to perform dedicated service"
                            value={values.qualities.abilityToPerform}
                            onChange={handleChange}
                          />
                          <QualityRating
                            name="qualities.professionalOutlook"
                            label="Professional Outlook"
                            value={values.qualities.professionalOutlook}
                            onChange={handleChange}
                          />
                          <QualityRating
                            name="qualities.sincerity"
                            label="Sincerity"
                            value={values.qualities.sincerity}
                            onChange={handleChange}
                          />
                          <QualityRating
                            name="qualities.honesty"
                            label="Honesty"
                            value={values.qualities.honesty}
                            onChange={handleChange}
                          />
                          <QualityRating
                            name="qualities.teamWork"
                            label="Ability for Team-work"
                            value={values.qualities.teamWork}
                            onChange={handleChange}
                          />
                          <QualityRating
                            name="qualities.discipline"
                            label="Discipline/Conduct"
                            value={values.qualities.discipline}
                            onChange={handleChange}
                          />
                          <TableRow>
                            <TableCell sx={{ border: '1px solid #ddd', p: 1.5, bgcolor: '#fafafa' }}>
                              <Typography variant="body2" fontWeight="bold">Any Other</Typography>
                              <TextField
                                name="qualities.anyOther"
                                value={values.qualities.anyOther}
                                onChange={handleChange}
                                placeholder="Specify quality"
                                variant="standard"
                                size="small"
                                fullWidth
                                sx={{ mt: 1 }}
                              />
                            </TableCell>
                            {['excellent', 'veryGood', 'average', 'belowAverage'].map((option) => (
                              <TableCell key={option} sx={{ border: '1px solid #ddd', textAlign: 'center', p: 1.5 }}>
                                <Radio
                                  name="qualities.anyOtherRating"
                                  value={option}
                                  checked={values.qualities.anyOtherRating === option}
                                  onChange={handleChange}
                                  size="small"
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Recommendation */}
                    <Typography variant="body2" paragraph sx={{ lineHeight: 2, mt: 3 }}>
                      <strong>In my opinion Mr./Ms. {values.candidateName} is the most suitable choice for</strong>
                    </Typography>
                    <TextField
                      name="candidatePosition"
                      value={values.candidatePosition}
                      onChange={handleChange}
                      placeholder="Position"
                      variant="standard"
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="body2" paragraph sx={{ lineHeight: 2 }}>
                      <strong>in your organization because</strong>
                    </Typography>
                    <TextField
                      name="recommendationText"
                      value={values.recommendationText}
                      onChange={handleChange}
                      error={touched.recommendationText && Boolean(errors.recommendationText)}
                      helperText={touched.recommendationText && errors.recommendationText}
                      placeholder="Recommendation (about 75 words)"
                      multiline
                      rows={4}
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {values.recommendationText ? `${values.recommendationText.split(' ').length} words` : '0 words'} 
                      {values.recommendationText && values.recommendationText.split(' ').length !== 75 && 
                        ` (Recommended: ~75 words)`
                      }
                    </Typography>

                    {/* Certifier Details */}
                    <Box sx={{ mt: 3, p: 3, border: '1px solid #ddd', borderRadius: 1, bgcolor: '#fafafa' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" fontWeight="bold">Certifier Name:</Typography>
                          <TextField
                            name="certifierName"
                            value={values.certifierName}
                            onChange={handleChange}
                            error={touched.certifierName && Boolean(errors.certifierName)}
                            helperText={touched.certifierName && errors.certifierName}
                            placeholder="Name"
                            variant="standard"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" fontWeight="bold">Designation:</Typography>
                          <TextField
                            name="certifierDesignation"
                            value={values.certifierDesignation}
                            onChange={handleChange}
                            error={touched.certifierDesignation && Boolean(errors.certifierDesignation)}
                            helperText={touched.certifierDesignation && errors.certifierDesignation}
                            placeholder="Designation"
                            variant="standard"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" fontWeight="bold">Organization:</Typography>
                          <TextField
                            name="certifierOrganization"
                            value={values.certifierOrganization}
                            onChange={handleChange}
                            error={touched.certifierOrganization && Boolean(errors.certifierOrganization)}
                            helperText={touched.certifierOrganization && errors.certifierOrganization}
                            placeholder="Organization"
                            variant="standard"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" fontWeight="bold">Contact Number:</Typography>
                          <TextField
                            name="certifierContact"
                            value={values.certifierContact}
                            onChange={handleChange}
                            error={touched.certifierContact && Boolean(errors.certifierContact)}
                            helperText={touched.certifierContact && errors.certifierContact}
                            placeholder="Contact Number"
                            variant="standard"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" fontWeight="bold">Official Seal:</Typography>
                          <TextField
                            name="officialSeal"
                            value={values.officialSeal}
                            onChange={handleChange}
                            placeholder="Official Seal / Stamp description"
                            variant="standard"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Date, Place, Signature */}
                    <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid #2c387e' }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" fontWeight="bold">Date:</Typography>
                          <TextField
                            name="date"
                            type="date"
                            value={values.date}
                            onChange={handleChange}
                            error={touched.date && Boolean(errors.date)}
                            helperText={touched.date && errors.date}
                            variant="standard"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" fontWeight="bold">Place:</Typography>
                          <TextField
                            name="place"
                            value={values.place}
                            onChange={handleChange}
                            error={touched.place && Boolean(errors.place)}
                            helperText={touched.place && errors.place}
                            placeholder="Place"
                            variant="standard"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" fontWeight="bold">Signature:</Typography>
                          <TextField
                            name="certifierSignature"
                            value={values.certifierSignature}
                            onChange={handleChange}
                            placeholder="Signature (text or upload)"
                            variant="standard"
                            fullWidth
                            sx={{ 
                              borderBottom: '2px solid #000',
                              bgcolor: '#fafafa',
                              p: 1
                            }}
                          />
                          <Typography variant="caption" fontWeight="bold" sx={{ mt: 1, display: 'block' }}>
                            {values.certifierName}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Paper>

                {/* Submit Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 2, 
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid #ddd'
                }} className="no-print">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onCancel}
                    disabled={isSubmitting}
                    startIcon={<CancelIcon />}
                    sx={{ px: 4 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={<SaveIcon />}
                    sx={{ px: 4 }}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Testimonial'}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default SB_FormatEdit;